import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { format } from 'date-fns';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { BASE_URL } from '../config';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidEmail } from '../utils/email';
import { showError, showSuccess } from '../utils/notifications';
import ComposeEmailModal from '../components/ComposeEmailModal';
import DateInput from './DateInput';
import AddressAutocomplete from './AddressAutocomplete';
import { applyAddressToForm, normalizeAddressValueForCreate } from '../utils/addressFormUtils';
import { findDefaultPayerId, payerForTypeChange, usesDefaultPayer } from "../utils/defaultTempPayer";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from '../utils/staffDocumentValidation';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'tempStaff', label: 'Temp Staff' },
  { key: 'outsourcedAgency', label: 'Outsourced Agency' },
];

const AssignmentDetailModal = ({ isOpen, onClose, assignment, openCreate = false }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [assignmentData, setAssignmentData] = useState(assignment || {});

  const [showDivisionModal, setShowDivisionModal] = useState(false);
  const [showCreateTempModal, setShowCreateTempModal] = useState(false);

  // Sync external request to open the create-temp form
  useEffect(() => {
    setShowCreateTempModal(Boolean(openCreate));
  }, [openCreate]);
  const [showCompose, setShowCompose] = useState(false);


  useEffect(() => {
    setAssignmentData(assignment || {});
  }, [assignment]);

  const [skills, setSkills] = useState([]);
  const [certs, setCerts] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedCerts, setSelectedCerts] = useState([]);
  const [uniform, setUniform] = useState([]);

  // Load master data once
  useEffect(() => {
    let mounted = true;
    const normalize = (json) => {
      if (!json) return [];
      if (Array.isArray(json)) return json;
      if (Array.isArray(json.data)) return json.data;
      if (Array.isArray(json.data?.data)) return json.data.data;

      // Deep search for the first array value in the object (reasonable depth)
      const visited = new Set();
      const queue = [json];
      while (queue.length) {
        const node = queue.shift();
        if (!node || typeof node !== 'object' || visited.has(node)) continue;
        visited.add(node);
        for (const k of Object.keys(node)) {
          try {
            const v = node[k];
            if (Array.isArray(v)) return v;
            if (v && typeof v === 'object') queue.push(v);
          } catch (e) {
            // ignore
          }
        }
      }

      return [];
    };

    const load = async () => {
      try {
  // backend exposes skills under the personnel route
  const sRes = await fetch(`${BASE_URL}/api/personnel/skills`);
        const sJson = await sRes.json();
        if (!mounted) return;
        const sList = normalize(sJson);
        console.log('Loaded skills response:', sJson);
        console.log('Normalized skills list:', sList);
        setSkills(sList);
      } catch (e) {
        console.error('Failed to load skills', e);
        if (mounted) setSkills([]);
      }

      try {
  // backend exposes certifications under the personnel route
  const cRes = await fetch(`${BASE_URL}/api/personnel/certifications`);
        const cJson = await cRes.json();
        if (!mounted) return;
        const cList = normalize(cJson);
        console.log('Loaded certifications response:', cJson);
        console.log('Normalized certifications list:', cList);
        setCerts(cList);
      } catch (e) {
        console.error('Failed to load certifications', e);
        if (mounted) setCerts([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  // Create Temp Staff modal: dropdown data
  const [divisionsList, setDivisionsList] = useState([]);
  const [payersList, setPayersList] = useState([]);
  const [recruitersList, setRecruitersList] = useState([]);
  const [payoutStaffList, setPayoutStaffList] = useState([]);

  useEffect(() => {
    let mounted = true;
    const normalize = (json) => {
      if (!json) return [];
      if (Array.isArray(json)) return json;
      if (Array.isArray(json.data)) return json.data;
      return [];
    };

    const load = async () => {
      try {
        const dRes = await fetch(`${BASE_URL}/api/divisions`);
        const dJson = await dRes.json();
        if (!mounted) return;
        setDivisionsList(normalize(dJson));
      } catch (e) {
        console.warn('Failed to load divisions', e);
        if (mounted) setDivisionsList([]);
      }

      try {
        const pRes = await fetch(`${BASE_URL}/api/accounting/payers`);
        const pJson = await pRes.json();
        if (!mounted) return;
        setPayersList(normalize(pJson));
      } catch (e) {
        console.warn('Failed to load payers', e);
        if (mounted) setPayersList([]);
      }

      try {
        // Recruiters: best-effort. Try personnel recruiters endpoint or fallback to accounts listing
        const rRes = await fetch(`${BASE_URL}/api/personnel/recruiters`);
        const rJson = await rRes.json().catch(() => ({}));
        if (!mounted) return;
        if (rJson && (Array.isArray(rJson) || Array.isArray(rJson.data))) setRecruitersList(normalize(rJson));
        else {
          // fallback: try generic accounts route
          const aRes = await fetch(`${BASE_URL}/api/personnel/staff`);
          const aJson = await aRes.json().catch(() => ({}));
          if (!mounted) return;
          setRecruitersList(normalize(aJson));
        }
      } catch (e) {
        console.warn('Failed to load recruiters', e);
        if (mounted) setRecruitersList([]);
      }

      try {
        // Payout staff: list of accounts (field staff)
        const psRes = await fetch(`${BASE_URL}/api/personnel/staff`);
        const psJson = await psRes.json();
        if (!mounted) return;
        setPayoutStaffList(normalize(psJson));
      } catch (e) {
        console.warn('Failed to load payout staff', e);
        if (mounted) setPayoutStaffList([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const [createTempData, setCreateTempData] = useState({
    type: 'fieldstaff',
    first_name: '',
    last_name: '',
    middle_name: '',
    preferred_name: '',
    email: '',
    gender: '',
    date_of_birth: '',
    mobile_phone: '',
    home_phone: '',
    work_phone: '',
    address: '',
    addressRaw: '',
    street: '',
    line1: '',
    line2: '',
    city: '',
    region: '',
    province: '',
    zip: '',
    postal_code: '',
    country: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    personal_number: '',
    personal_email: '',
    division_id: '',
    recruiter_account_id: '',
    residency_status: '',
    payroll_payer_id: '',
    payout_staff_id: '',
    sin: '',
    hst: ''
  });

  const [createErrors, setCreateErrors] = useState({});
  const [creatingTempStaff, setCreatingTempStaff] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const setField = (key, value) => {
    setCreateTempData(prev => ({ ...prev, [key]: value }));
  };

  // Candidate / Field Staff start with the "Cheque - 44hrs" payer once payers have loaded
  useEffect(() => {
    if (!usesDefaultPayer(createTempData.type) || createTempData.payroll_payer_id) return;
    const defaultPayerId = findDefaultPayerId(payersList);
    if (defaultPayerId) setField('payroll_payer_id', defaultPayerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payersList]);

  const requiresPermitUpload = ['study_resident', 'temporary_resident', 'other'].includes(createTempData.residency_status);

  const requiredFieldsByType = {
    candidate: [],
    fieldstaff: ['address', 'emergency_contact_name', 'emergency_contact_phone', 'payroll_payer_id'],
    outsourcedstaff: ['payroll_payer_id'],
  };
  const typeRequiredFields = requiredFieldsByType[createTempData.type] || requiredFieldsByType.candidate;
  const isRequired = (field) => typeRequiredFields.includes(field);
  // Email and mobile are required for every type except Outsourced Staff
  const contactRequired = createTempData.type !== 'outsourcedstaff';
  const payerFieldLabel = createTempData.type === 'outsourcedstaff' ? 'Agency' : 'Payroll Payer';

  const getResidencyTitle = () => {
    switch (createTempData.residency_status) {
      case 'study_resident': return 'Study Permit';
      case 'temporary_resident': return 'Work Permit';
      case 'other': return 'Refugee document';
      default: return '';
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validationMessage = validateStaffDocumentFile(file);
      if (validationMessage) return showError(validationMessage);
      setSelectedFile(file);
      setCreateErrors(prev => ({ ...prev, permitDocument: '' }));
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validationMessage = validateStaffDocumentFile(file);
      if (validationMessage) {
        showError(validationMessage);
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      setCreateErrors(prev => ({ ...prev, permitDocument: '' }));
    }
  };

  const handleViewSelectedFile = () => {
    if (!selectedFile) return;
    const url = URL.createObjectURL(selectedFile);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handleDownloadSelectedFile = () => {
    if (!selectedFile) return;
    const url = URL.createObjectURL(selectedFile);
    const link = document.createElement('a');
    link.href = url;
    link.download = selectedFile.name || 'residency-document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const validateField = (key, value) => {
    const v = String(value || '').trim();
    let err = '';
    if (key === 'first_name' || key === 'last_name') {
      if (!v) err = 'Required';
    }
    if (key === 'email' || key === 'personal_email') {
      if (key === 'email' && !v) { if (contactRequired) err = 'Required'; }
      else if (v && !isValidEmail(v)) err = 'Invalid email';
    }
    if (key === 'mobile_phone') {
      if (!v) { if (contactRequired) err = 'Required'; }
      else if (!isValidPhoneNumber(v)) err = 'Invalid phone';
    }
    if (key === 'division_id') {
      if (!v) err = 'Required';
    }
    if (key === 'address' && isRequired('address')) {
      if (!createTempData.addressRaw && !createTempData.address && !createTempData.street && !createTempData.line1) err = 'Required';
    }
    if (key === 'emergency_contact_name' && isRequired('emergency_contact_name')) {
      if (!v) err = 'Required';
    }
    if (key === 'emergency_contact_phone') {
      if (isRequired('emergency_contact_phone') && !v) err = 'Required';
      else if (v && !isValidPhoneNumber(v)) err = 'Invalid phone';
    }
    if (key === 'payroll_payer_id' && isRequired('payroll_payer_id')) {
      if (!v) err = `Select ${payerFieldLabel.toLowerCase()}`;
    }
    if (key === 'sin') {
      if (v && !/^[0-9]{9}$/.test(v)) err = 'SIN must be 9 digits';
    }

    setCreateErrors(prev => ({ ...prev, [key]: err }));
    return err;
  };

  const validateAll = () => {
    const required = ['first_name', 'last_name', 'email', 'mobile_phone', 'division_id'];
    if (isRequired('address')) required.push('address');
    if (isRequired('emergency_contact_name')) required.push('emergency_contact_name');
    if (isRequired('emergency_contact_phone')) required.push('emergency_contact_phone');
    if (isRequired('payroll_payer_id')) required.push('payroll_payer_id');

    const next = {};
    required.forEach(k => {
      const err = validateField(k, createTempData[k]);
      if (err) next[k] = err;
    });

    if (requiresPermitUpload && !selectedFile) {
      next.permitDocument = `Please upload ${getResidencyTitle()}`;
    } else {
      next.permitDocument = '';
    }

    const sinErr = validateField('sin', createTempData.sin);
    const personalEmailErr = validateField('personal_email', createTempData.personal_email);
    if (sinErr) next.sin = sinErr;
    if (personalEmailErr) next.personal_email = personalEmailErr;

    setCreateErrors(prev => ({ ...prev, ...next }));
    const combined = { ...createErrors, ...next };
    const hasError = Object.values(combined).some(v => v && v.length > 0);
    return !hasError;
  };

  const handleCreateTempSave = async () => {
    const ok = validateAll();
    if (!ok) {
      try {
        const first = document.querySelector('.fixed.inset-0 .border-red-500');
        if (first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {}
      return;
    }

    const payload = {
      type: createTempData.type || 'fieldstaff',
      firstName: String(createTempData.first_name || '').trim(),
      lastName: String(createTempData.last_name || '').trim(),
      middle_name: String(createTempData.middle_name || '').trim() || null,
      preferred_name: String(createTempData.preferred_name || '').trim() || null,
      email: String(createTempData.email || '').trim(),
      gender: createTempData.gender || null,
      date_of_birth: createTempData.date_of_birth || null,
      mobile: createTempData.mobile_phone || '',
      emergencyName: String(createTempData.emergency_contact_name || '').trim(),
      emergencyPhone: createTempData.emergency_contact_phone || '',
      personalNumber: String(createTempData.personal_number || '').trim(),
      personalEmail: String(createTempData.personal_email || '').trim(),
      employmentOfficeId: createTempData.division_id || '',
      recruiterAccountId: createTempData.recruiter_account_id || '',
      residencyStatus: createTempData.residency_status || '',
      payrollPayerId: createTempData.payroll_payer_id || '',
      payoutStaffAccountId: createTempData.payout_staff_id || '',
      sin: String(createTempData.sin || '').trim(),
      hstNumber: String(createTempData.hst || '').trim(),
      address: createTempData.address || createTempData.street || '',
      addressRaw: createTempData.addressRaw || null,
      line1: createTempData.line1 || createTempData.street || null,
      line2: createTempData.line2 || null,
      city: createTempData.city || null,
      province: createTempData.province || createTempData.region || null,
      postal_code: createTempData.postal_code || createTempData.zip || null,
      country: createTempData.country || null,
    };

    try {
      setCreatingTempStaff(true);
      const res = await fetch(`${BASE_URL}/api/personnel/temp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to create temp staff');
      }

      const accountId = data?.accountId || data?.created?.account_id || data?.id;
      if (selectedFile && accountId) {
        try {
          const mapToDocType = (v) => {
            switch (String(v)) {
              case 'study_resident': return 'study_permit';
              case 'temporary_resident': return 'work_permit';
              case 'other': return 'refugee';
              default: return null;
            }
          };
          const docType = mapToDocType(createTempData.residency_status);
          if (docType) {
            const fd = new FormData();
            fd.append('file', selectedFile);
            fd.append('docType', docType);
            const linkRes = await fetch(`${BASE_URL}/api/registration/${accountId}/upload-and-link`, {
              method: 'POST',
              credentials: 'include',
              body: fd,
            });
            const linkJson = await linkRes.json().catch(() => ({}));
            if (!linkRes.ok) {
              console.warn('upload-and-link failed', linkJson);
            }
          }
        } catch (e) {
          console.warn('Failed uploading permit file:', e);
        }
      }

      showSuccess('Temp staff created');
      setSelectedFile(null);
      if (typeof onClose === 'function') onClose();
      else setShowCreateTempModal(false);
    } catch (error) {
      showError(error?.message || 'Failed to create temp staff');
    } finally {
      setCreatingTempStaff(false);
    }
  };

  const renderCreateTempModalContent = (closeHandler) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[50200] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Create Temp Staff</h3>
          <button onClick={closeHandler} className="text-gray-500 hover:text-gray-700 text-base">✖</button>
        </div>

        {/* Body: scrollable area */}
        <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-4 text-sm">
          {/* Staff Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Type*</label>
            <select
              value={createTempData.type}
              onChange={e => {
                const v = e.target.value;
                setCreateTempData(prev => ({
                  ...prev,
                  type: v,
                  payroll_payer_id: payerForTypeChange(v, prev.payroll_payer_id, payersList),
                }));
                setCreateErrors(prev => ({ ...prev, emergency_contact_phone: '', address: '', payroll_payer_id: '', emergency_contact_name: '' }));
              }}
              className="w-full border rounded px-3 py-2"
            >
              <option value="candidate">Candidate</option>
              <option value="fieldstaff">Field Staff</option>
              <option value="outsourcedstaff">Outsourced Staff</option>
            </select>
          </div>

          {/* Personal Information */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium">First Name*</label>
              <input
                className={`w-full border rounded px-3 py-2 ${createErrors.first_name ? 'border-red-500' : ''}`}
                value={createTempData.first_name || ''}
                onChange={e => { const v = e.target.value; setField('first_name', v); validateField('first_name', v); }}
                autoComplete="off"
              />
              {createErrors.first_name && <div className="text-red-600 text-xs mt-1">{createErrors.first_name}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium">Last Name*</label>
              <input
                className={`w-full border rounded px-3 py-2 ${createErrors.last_name ? 'border-red-500' : ''}`}
                value={createTempData.last_name || ''}
                onChange={e => { const v = e.target.value; setField('last_name', v); validateField('last_name', v); }}
                autoComplete="off"
              />
              {createErrors.last_name && <div className="text-red-600 text-xs mt-1">{createErrors.last_name}</div>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium">Middle Name</label>
              <input
                className="w-full border rounded px-3 py-2"
                value={createTempData.middle_name || ''}
                onChange={e => setField('middle_name', e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Preferred Name</label>
              <input
                className="w-full border rounded px-3 py-2"
                value={createTempData.preferred_name || ''}
                onChange={e => setField('preferred_name', e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium">Email{contactRequired && '*'}</label>
              <input
                type="email"
                className={`w-full border rounded px-3 py-2 ${createErrors.email ? 'border-red-500' : ''}`}
                value={createTempData.email || ''}
                onChange={e => { const v = e.target.value; setField('email', v); validateField('email', v); }}
                autoComplete="off"
              />
              {createErrors.email && <div className="text-red-600 text-xs mt-1">{createErrors.email}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium">Gender</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={createTempData.gender || ''}
                onChange={e => setField('gender', e.target.value)}
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium">Date of Birth</label>
              <DateInput
                className="w-full border rounded px-3 py-2"
                value={createTempData.date_of_birth || ''}
                onChange={val => setField('date_of_birth', val)}
                min="1900-01-01"
              />
            </div>
            <div />
          </div>

          {/* Contact Information Section */}
          <div className="mt-4 mb-2">
            <h3 className="text-base font-semibold text-gray-700 border-b pb-2">Contact Information</h3>
          </div>

          <div>
            <label className="block text-sm font-medium">
              Mobile number on record with Talent{contactRequired && '*'}
            </label>
            <PhoneInput
              value={createTempData.mobile_phone || ''}
              onChange={v => { setField('mobile_phone', v); validateField('mobile_phone', v); }}
              defaultCountry="CA"
              international
              withCountryCallingCode
              className={`w-full mb-2 border rounded px-3 py-2 ${createErrors.mobile_phone ? 'border-red-500' : ''}`}
            />
            {createErrors.mobile_phone && <div className="text-red-600 text-xs mt-1">{createErrors.mobile_phone}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium">
              Address{isRequired('address') ? '*' : ''}
            </label>
            <AddressAutocomplete
              value={createTempData.addressRaw || createTempData.address || ''}
              onChange={v => {
                applyAddressToForm(setField, v);
                const normalized = normalizeAddressValueForCreate(v);
                validateField('address', normalized.address);
              }}
            />
            {createErrors.address && <div className="text-red-600 text-xs mt-1">{createErrors.address}</div>}
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium">
                Emergency Contact Name{isRequired('emergency_contact_name') ? '*' : ''}
              </label>
              <input
                className={`w-full border rounded px-3 py-2 ${createErrors.emergency_contact_name ? 'border-red-500' : ''}`}
                value={createTempData.emergency_contact_name || ''}
                onChange={e => { const v = e.target.value; setField('emergency_contact_name', v); validateField('emergency_contact_name', v); }}
              />
              {createErrors.emergency_contact_name && <div className="text-red-600 text-xs mt-1">{createErrors.emergency_contact_name}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Emergency Contact Number{isRequired('emergency_contact_phone') ? '*' : ''}
              </label>
              <PhoneInput
                value={createTempData.emergency_contact_phone || ''}
                onChange={v => { setField('emergency_contact_phone', v); validateField('emergency_contact_phone', v); }}
                defaultCountry="CA"
                international
                withCountryCallingCode
                className={`w-full border rounded px-3 py-2 ${createErrors.emergency_contact_phone ? 'border-red-500' : ''}`}
              />
              {createErrors.emergency_contact_phone && <div className="text-red-600 text-xs mt-1">{createErrors.emergency_contact_phone}</div>}
            </div>
          </div>

          {/* Employment Information Section */}
          <div className="mt-4 mb-2">
            <h3 className="text-base font-semibold text-gray-700 border-b pb-2">Employment Information</h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium">Employment Office*</label>
              <select
                className={`w-full border rounded px-3 py-2 ${createErrors.division_id ? 'border-red-500' : ''}`}
                value={createTempData.division_id || ''}
                onChange={e => { const v = e.target.value; setField('division_id', v); validateField('division_id', v); }}
              >
                <option value="">Select</option>
                {divisionsList.map(d => (
                  <option key={d.division_id || d.id || d.divisionId} value={d.division_id || d.id || d.divisionId}>
                    {d.name || d.title || d.division_name || `#${d.division_id || d.id}`}
                  </option>
                ))}
              </select>
              {createErrors.division_id && <div className="text-red-600 text-xs mt-1">{createErrors.division_id}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium">Recruiter</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={createTempData.recruiter_account_id || ''}
                onChange={e => setField('recruiter_account_id', e.target.value)}
              >
                <option value="">Select</option>
                {recruitersList.map(r => (
                  <option key={r.account_id || r.id} value={r.account_id || r.id}>
                    {r.name || `${r.first_name || ''} ${r.last_name || ''}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Payroll Information Section */}
          <div className="mt-4 mb-2">
            <h3 className="text-base font-semibold text-gray-700 border-b pb-2">Payroll Information</h3>
          </div>

          <div>
            <div>
              <label className="block text-sm font-medium">Residency Status</label>
              <select
                className={`w-full border rounded px-3 py-2 ${createErrors.residency_status ? 'border-red-500' : ''}`}
                value={createTempData.residency_status || ''}
                onChange={e => { const v = e.target.value; setField('residency_status', v); validateField('residency_status', v); }}
              >
                <option value="">Select</option>
                <option value="citizen">Canadian Citizen / Permanent Resident</option>
                <option value="study_resident">Study permit</option>
                <option value="temporary_resident">Work permit</option>
                <option value="other">Refugee</option>
              </select>
              {createErrors.residency_status && <div className="text-red-600 text-xs mt-1">{createErrors.residency_status}</div>}

              {/* Permit Upload Section */}
              {requiresPermitUpload && (
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">
                    {getResidencyTitle()}*
                  </label>

                  {selectedFile ? (
                    <div className="flex items-center justify-between bg-gray-100 border rounded p-3 text-sm">
                      <div className="min-w-0">
                        <span className="text-sm text-gray-700 break-words">{selectedFile.name}</span>
                      </div>
                      <div className="flex items-center gap-3 ml-3">
                        <button type="button" className="text-sm text-blue-600 hover:underline" onClick={handleViewSelectedFile}>View</button>
                        <button type="button" className="text-sm text-blue-600 hover:underline" onClick={handleDownloadSelectedFile}>Download</button>
                        <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => { setSelectedFile(null); setCreateErrors(prev => ({ ...prev, permitDocument: '' })); }}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400 bg-gray-50"
                      } ${createErrors.permitDocument ? "border-red-500" : ""}`}
                      onClick={() => document.getElementById("temp-staff-permit-file-input")?.click()}
                    >
                      <input
                        id="temp-staff-permit-file-input"
                        type="file"
                        className="hidden"
                        accept={STAFF_DOCUMENT_ACCEPT}
                        onChange={handleFileSelect}
                      />
                      <div className="flex flex-col items-center justify-center gap-1">
                        <span className="text-gray-600 text-sm">Drag & Drop your file</span>
                        <span className="text-xs text-gray-400">or</span>
                        <span className="mt-1 inline-block bg-red-600 text-white px-4 py-1.5 rounded text-sm hover:bg-red-700">Upload</span>
                      </div>
                    </div>
                  )}
                  {createErrors.permitDocument && (
                    <div className="text-red-600 text-xs mt-1">{createErrors.permitDocument}</div>
                  )}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-sm font-medium">
                  {payerFieldLabel}{isRequired('payroll_payer_id') ? '*' : ''}
                </label>
                <select
                  className={`w-full border rounded px-3 py-2 ${createErrors.payroll_payer_id ? 'border-red-500' : ''}`}
                  value={createTempData.payroll_payer_id || ''}
                  onChange={e => { const v = e.target.value; setField('payroll_payer_id', v); validateField('payroll_payer_id', v); }}
                >
                  <option value="">{`Select ${payerFieldLabel}`}</option>
                  {payersList.map(p => (
                    <option key={p.payer_id || p.id} value={p.payer_id || p.id}>
                      {p.name || p.legal_name || `#${p.payer_id || p.id}`}
                    </option>
                  ))}
                </select>
                {createErrors.payroll_payer_id && <div className="text-red-600 text-xs mt-1">{createErrors.payroll_payer_id}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium">Payout Staff</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={createTempData.payout_staff_id || ''}
                  onChange={e => setField('payout_staff_id', e.target.value)}
                >
                  <option value="">Select Staff</option>
                  {payoutStaffList.map(p => (
                    <option key={p.account_id || p.id} value={p.account_id || p.id}>
                      {p.name || `${p.first_name || ''} ${p.last_name || ''}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-sm font-medium">Social Insurance Number</label>
                <input
                  className={`w-full border rounded px-3 py-2 ${createErrors.sin ? 'border-red-500' : ''}`}
                  value={createTempData.sin || ''}
                  onChange={e => { const v = e.target.value; setField('sin', v); validateField('sin', v); }}
                  autoComplete="off"
                />
                {createErrors.sin && <div className="text-red-600 text-xs mt-1">{createErrors.sin}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium">HST Number{isRequired('hst') ? '*' : ''}</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={createTempData.hst || ''}
                  onChange={e => setField('hst', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer: fixed controls */}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
          <button onClick={closeHandler} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Cancel</button>
          <button
            onClick={handleCreateTempSave}
            disabled={creatingTempStaff}
            className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm ${creatingTempStaff ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {creatingTempStaff ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  // Helpers to extract id/label from various possible shapes
  const getId = (item) => item?.skill_id ?? item?.certification_id ?? item?.id ?? item?.skillId ?? item?.certificationId ?? item?.value ?? item?.key ?? null;
  const getLabel = (item) => item?.name ?? item?.label ?? item?.title ?? item?.skill_name ?? item?.certification_name ?? String(getId(item) ?? '');

  // Parse stored requirements from assignment.note/admin_note/staff_note
  useEffect(() => {
    const raw = assignmentData?.note || assignmentData?.staff_note || assignmentData?.admin_note || '';
    try {
      const parsed = raw ? JSON.parse(raw) : {};
      setSelectedSkills(Array.isArray(parsed.skills) ? parsed.skills : []);
      setSelectedCerts(Array.isArray(parsed.certifications) ? parsed.certifications : []);
      setUniform(Array.isArray(parsed.uniform) ? parsed.uniform : []);
    } catch (e) {
      // not JSON — ignore
      setSelectedSkills([]);
      setSelectedCerts([]);
      setUniform([]);
    }
  }, [assignmentData]);

  // Do NOT automatically overwrite assignmentData.note on mount.
  // We'll build the payload only when the user explicitly saves to avoid clobbering existing notes.
  const buildRequirementsPayload = () => ({ skills: selectedSkills, certifications: selectedCerts, uniform });

  // action handlers for the footer/header buttons
  const handleSave = async () => {
    // Prepare the payload and update local note before persisting
    const payload = buildRequirementsPayload();
    const updated = { ...assignmentData, note: JSON.stringify(payload) };
    setAssignmentData(updated);
    console.log('Save assignment (local only):', updated);
    // TODO: call backend API to persist 'updated' to the correct endpoint/field
    if (onClose) onClose();
  };

  const handleDuplicate = () => {
    console.log('Duplicate assignment', assignmentData);
    // TODO: implement duplication flow
  };

  const handleSchedulerView = () => {
    console.log('Open scheduler view for assignment', assignmentData);
    // TODO: integrate with scheduler
  };

  // Allow opening the Create Temp Staff form even when there's no assignment passed.
  if (!isOpen && !openCreate) return null;

  // If parent requested the create form but did not provide an assignment, render
  // just the Create Temp Staff modal so it appears centered on screen.
  if (openCreate && !assignment) {
    const closeHandler = () => { if (typeof onClose === 'function') onClose(); else setShowCreateTempModal(false); };
    const modal = renderCreateTempModalContent(closeHandler);
    if (typeof document !== 'undefined') return ReactDOM.createPortal(modal, document.body);
    return modal;
  }

  const fullModal = (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[50000] flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col m-4" onClick={e => e.stopPropagation()}>
        <div className="flex flex-col md:flex-row h-full">
          {/* Sidebar - tabs on the left to match other detail modals */}
          <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-gray-200 p-4 bg-gray-50 flex-shrink-0 flex flex-col md:flex-col gap-3 md:gap-2">
            <div>
              <h2 className="text-lg md:text-xl font-semibold">Assignment</h2>
              <h6 className="text-xs md:text-sm text-gray-500">#{assignment.assignment}</h6>
            </div>
            <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pr-2 pb-1 md:pb-0 scrollbar-none">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 md:px-3 md:py-2 rounded text-sm md:text-base text-center md:text-left whitespace-nowrap ${
                    activeTab === tab.key
                      ? "bg-sidebar-active text-white"
                      : "hover:bg-gray-200 bg-gray-100 md:bg-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">{assignment.clientName || 'Client Name'} - {assignment.locationName || 'Location Name'}</h2>
                <h6 className="text-sm text-gray-500">{TABS.find(t => t.key === activeTab)?.label} Information</h6>
              </div>
              <div className="flex gap-2">
                {activeTab === 'overview' ? (
                  <>
                    <button
                      onClick={() => setShowCompose(true)}
                      className="px-3 py-2 rounded border text-sm hover:bg-gray-100"
                    >
                      Notify Staff
                    </button>
                    <button
                      className="px-3 py-2 rounded border text-sm hover:bg-gray-100"
                      onClick={() => setShowDivisionModal(true)}
                    >
                      Modify Division and Position
                    </button>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800">✖</button>
                  </>
                ) : (
                  <button onClick={onClose} className="text-gray-500 hover:text-gray-800">✖</button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status*</label>
                      <select
                        value={assignmentData?.state || assignmentData?.status || ''}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, state: e.target.value }))}
                        className="w-full border rounded px-3 py-2 bg-white"
                      >
                        <option value="unassigned">Unassigned</option>
                        <option value="tentative">Tentative</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="declined">Unfilled</option>
                        <option value="deleted">Deleted</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assignment ID</label>
                      <div className="mb-4">
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          className="text-red-600 font-medium text-lg hover:text-red-700 cursor-pointer no-underline border-0 bg-transparent p-0"
                        >
                          #{assignmentData.assignment || assignment.assignment || '—'}
                        </button>
                      </div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">Assigned to</label>
                      <div className="text-gray-900">
                        <div>
                          {assignmentData?.assigned_staff_name || assignment?.assigned_staff_name || 'Unassigned'}
                          {assignmentData?.staff_payment_method && ` (${assignmentData.staff_payment_method})`}
                        </div>
                      </div>
                      {assignmentData?.assigned_staff_phone && (
                        <div className="text-sm text-gray-600 mt-1">{assignmentData.assigned_staff_phone}</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start*</label>
                      <input
                        type="datetime-local"
                        value={assignmentData?.start_date ? format(new Date(assignmentData.start_date), "yyyy-MM-dd'T'HH:mm") : ''}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, start_date: e.target.value }))}
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End*</label>
                      <input
                        type="datetime-local"
                        value={assignmentData?.end_date ? format(new Date(assignmentData.end_date), "yyyy-MM-dd'T'HH:mm") : ''}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, end_date: e.target.value }))}
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pay Rate*</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={assignmentData?.pay_rate || assignmentData?.payRate || ''}
                          onChange={(e) => setAssignmentData(prev => ({ ...prev, pay_rate: e.target.value }))}
                          className="w-full border rounded px-7 py-2"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Travel Time</label>
                      <div className="text-gray-900">{assignmentData.travelTimeType || assignment.travelTimeType || 'No Travel Time'}</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={assignmentData?.unpaid_break === 30}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, unpaid_break: e.target.checked ? 30 : 0 }))}
                        className="rounded border-gray-300"
                      />
                      <span>Unpaid 30 Minute Break</span>
                    </label>

                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={Boolean(assignmentData?.notify_staff_on_change)}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, notify_staff_on_change: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      <span>Staff Requires Notification of Change</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shift Supervisor*</label>
                    <input
                      value={assignmentData?.supervisor_name || assignment?.supervisor_name || ''}
                      disabled
                      className="w-full border rounded px-3 py-2 bg-gray-100"
                    />
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Requirements</h3>
                    <div className="w-1/3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                      <select
                        value={assignmentData?.gender || ''}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, gender: e.target.value }))}
                        className="w-full border rounded px-3 py-2"
                      >
                        <option value="">Any</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Skills</label>
                      <Select
                        isMulti
                        placeholder="Select skills..."
                        options={skills.map(s => ({ value: getId(s), label: getLabel(s) }))}
                        noOptionsMessage={() => 'No skills available'}
                        value={(selectedSkills || []).map(id => {
                          const match = skills.find(s => String(getId(s)) === String(id));
                          return { value: id, label: match ? getLabel(match) : String(id) };
                        })}
                        onChange={(sel) => setSelectedSkills((sel || []).map(s => s.value))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Certifications</label>
                      <Select
                        isMulti
                        placeholder="Select certifications..."
                        options={certs.map(c => ({ value: getId(c), label: getLabel(c) }))}
                        noOptionsMessage={() => 'No certifications available'}
                        value={(selectedCerts || []).map(id => {
                          const match = certs.find(c => String(getId(c)) === String(id));
                          return { value: id, label: match ? getLabel(match) : String(id) };
                        })}
                        onChange={(sel) => setSelectedCerts((sel || []).map(s => s.value))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Uniform</label>
                      <CreatableSelect
                        isMulti
                        placeholder="Type or select uniform items..."
                        options={(uniform || []).map(u => ({ value: u, label: u }))}
                        noOptionsMessage={() => 'Type items to add uniform'}
                        value={(uniform || []).map(u => ({ value: u, label: u }))}
                        onChange={(sel) => setUniform((sel || []).map(s => s.value))}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note to Temp Staff</label>
                    <textarea
                      rows={3}
                      value={assignmentData?.staff_note || assignmentData?.note || ''}
                      onChange={(e) => setAssignmentData(prev => ({ ...prev, staff_note: e.target.value }))}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Admin Note</label>
                    <textarea
                      rows={3}
                      value={assignmentData?.admin_note || assignmentData?.adminNote || ''}
                      onChange={(e) => setAssignmentData(prev => ({ ...prev, admin_note: e.target.value }))}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'tempStaff' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">Temp Staff</h3>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Search temp staff..." className="border rounded px-3 py-2" />
                      <button className="p-2 hover:bg-gray-100 rounded" title="Filter">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded" title="Refresh">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      <button onClick={() => setShowCreateTempModal(true)} className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700">Create</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Temp Staff</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Proximity</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Reliability</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Utilization</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Hours</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Assign</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="hover:bg-red-50 transition-colors">
                          <td className="px-4 py-3">John Doe</td>
                          <td className="px-4 py-3">Field Staff</td>
                          <td className="px-4 py-3">5.2 km</td>
                          <td className="px-4 py-3">98%</td>
                          <td className="px-4 py-3">80%</td>
                          <td className="px-4 py-3">32/40h</td>
                          <td className="px-4 py-3">
                            <button className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs">Assign</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'outsourcedAgency' && (
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-medium">Outsourced Agency</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Outsourced Agency</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Assign</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="hover:bg-red-50 transition-colors">
                          <td className="px-4 py-3">Agency XYZ</td>
                          <td className="px-4 py-3">
                            <button className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs">Assign</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {activeTab === 'overview' && (
              <div className="border-t p-4 flex justify-end space-x-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDuplicate}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Save and Duplicate Shift
                </button>
                <button
                  onClick={handleSchedulerView}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Scheduler View
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
  {showDivisionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[50100] flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Modify Division and Position</h3>
              <button onClick={() => setShowDivisionModal(false)} className="text-gray-500 hover:text-gray-700">✖</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Division*</label>
                <select
                  value={assignmentData?.division_id || ''}
                  onChange={(e) => setAssignmentData(prev => ({ ...prev, division_id: e.target.value }))}
                  className="w-full border rounded px-3 py-2 bg-white"
                >
                  <option value="">Select division</option>
                  <option value="1">Hospitality Ottawa</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position*</label>
                <select
                  value={assignmentData?.position_id || ''}
                  onChange={(e) => setAssignmentData(prev => ({ ...prev, position_id: e.target.value }))}
                  className="w-full border rounded px-3 py-2 bg-white"
                >
                  <option value="">Select position</option>
                  <option value="1">Cleaner</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <button onClick={() => setShowDivisionModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">Cancel</button>
              <button onClick={() => setShowDivisionModal(false)} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Save</button>
            </div>
          </div>
        </div>
      )}
      {showCreateTempModal && renderCreateTempModalContent(() => setShowCreateTempModal(false))}
      {showCompose && (
        <ComposeEmailModal isOpen={showCompose} onClose={() => setShowCompose(false)} prefill={{ recipients: [] }} />
      )}
    </div>
  </div>
  );
  if (typeof document !== 'undefined') return ReactDOM.createPortal(fullModal, document.body);
  return fullModal;
};

export default AssignmentDetailModal;
